import { BillingRecordPicker } from "../components/billing/BillingRecordPicker";
import {
  RecurringWorkspace,
  RecurringSummary,
  recurringError,
} from "../components/billing/RecurringWorkspace";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Input,
  Textarea,
  Select,
  Option,
  Switch,
  Card,
  CardContent,
  Stack,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/joy";
import {
  Plus as Add,
  Trash as Delete,
  ArrowLeft as ArrowBack,
} from "@phosphor-icons/react";
import { recurringInvoicesApi } from "../lib/billingApi";
import {
  PAYMENT_TERMS,
  normalizePaymentTerms,
} from "../components/billing/paymentTerms";
import type {
  RecurringInvoiceTemplate,
  RecurringFrequency,
  RecurringAmountType,
  RecurringLineItemTemplate,
} from "../types/billing";

interface FormState {
  name: string;
  description: string;
  assigned_to: number | null;
  source_job: number | null;
  agreement_reference: string;
  customer: number | null;
  frequency: RecurringFrequency;
  frequency_interval: number;
  amount_type: RecurringAmountType;
  fixed_amount: string;
  percentage_base: string;
  percentage_rate: number;
  start_date: string;
  end_date: string;
  next_invoice_date: string;
  max_occurrences: string;
  default_payment_terms: string;
  default_notes: string;
  line_items_template: RecurringLineItemTemplate[];
  is_auto_send: boolean;
  is_auto_approve: boolean;
  notify_customer: boolean;
  notify_assigned_user: boolean;
  notification_days_advance: number;
}

const today = () => new Date().toISOString().split("T")[0];

const emptyForm = (): FormState => ({
  name: "",
  description: "",
  assigned_to: null,
  source_job: null,
  agreement_reference: "",
  customer: null,
  frequency: "monthly",
  frequency_interval: 1,
  amount_type: "fixed",
  fixed_amount: "",
  percentage_base: "",
  percentage_rate: 0,
  start_date: today(),
  end_date: "",
  next_invoice_date: today(),
  max_occurrences: "",
  default_payment_terms: "net_30",
  default_notes: "",
  line_items_template: [],
  is_auto_send: false,
  is_auto_approve: false,
  notify_customer: true,
  notify_assigned_user: true,
  notification_days_advance: 3,
});

export default function RecurringInvoiceForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const templateId = id ? Number(id) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(emptyForm());

  // Load existing template for edit
  const {
    data: existing,
    isLoading: existingLoading,
    error: existingError,
  } = useQuery({
    queryKey: ["recurring-template", templateId],
    queryFn: () => recurringInvoicesApi.get(templateId as number),
    enabled: isEdit && templateId != null,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name ?? "",
        description: existing.description ?? "",
        customer: existing.customer ?? null,
        assigned_to: existing.assigned_to ?? null,
        source_job: existing.source_job ?? null,
        agreement_reference: existing.agreement_reference ?? "",
        frequency: existing.frequency ?? "monthly",
        frequency_interval: existing.frequency_interval ?? 1,
        amount_type: existing.amount_type ?? "fixed",
        fixed_amount: existing.fixed_amount ?? "",
        percentage_base: existing.percentage_base ?? "",
        percentage_rate: existing.percentage_rate ?? 0,
        start_date: existing.start_date ?? today(),
        end_date: existing.end_date ?? "",
        next_invoice_date: existing.next_invoice_date ?? today(),
        max_occurrences:
          existing.max_occurrences != null
            ? String(existing.max_occurrences)
            : "",
        // Templates written while this was free text can hold anything.
        default_payment_terms: normalizePaymentTerms(
          existing.default_payment_terms,
        ),
        default_notes: existing.default_notes ?? "",
        line_items_template: existing.line_items_template ?? [],
        is_auto_send: existing.is_auto_send ?? false,
        is_auto_approve: existing.is_auto_approve ?? false,
        notify_customer: existing.notify_customer ?? true,
        notify_assigned_user: existing.notify_assigned_user ?? true,
        notification_days_advance: existing.notification_days_advance ?? 3,
      });
    }
  }, [existing]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Line item helpers
  const addLineItem = () => {
    setField("line_items_template", [
      ...form.line_items_template,
      { description: "", quantity: 1, unit_price: 0 },
    ]);
  };

  const updateLineItem = (
    index: number,
    updates: Partial<RecurringLineItemTemplate>,
  ) => {
    const next = form.line_items_template.map((li, i) =>
      i === index ? { ...li, ...updates } : li,
    );
    setField("line_items_template", next);
  };

  const removeLineItem = (index: number) => {
    setField(
      "line_items_template",
      form.line_items_template.filter((_, i) => i !== index),
    );
  };

  const buildPayload = (): Partial<RecurringInvoiceTemplate> => ({
    name: form.name,
    description: form.description,
    customer: form.customer as number,
    ...(form.assigned_to ? { assigned_to: form.assigned_to } : {}),
    source_job: form.source_job,
    agreement_reference: form.agreement_reference,
    frequency: form.frequency,
    frequency_interval: form.frequency_interval,
    amount_type: form.amount_type,
    fixed_amount: form.amount_type === "fixed" ? form.fixed_amount || "0" : "0",
    percentage_base:
      form.amount_type === "percentage" ? form.percentage_base || "0" : "0",
    percentage_rate:
      form.amount_type === "percentage" ? form.percentage_rate : 0,
    start_date: form.start_date,
    end_date: form.end_date || null,
    next_invoice_date: form.next_invoice_date,
    max_occurrences: form.max_occurrences ? Number(form.max_occurrences) : null,
    default_payment_terms: form.default_payment_terms,
    default_notes: form.default_notes,
    line_items_template: form.line_items_template.map((li) => ({
      description: li.description,
      quantity: Number(li.quantity) || 0,
      unit_price: Number(li.unit_price) || 0,
      ...(li.tax_rate != null ? { tax_rate: Number(li.tax_rate) } : {}),
    })),
    is_auto_send: form.is_auto_send,
    is_auto_approve: form.is_auto_approve,
    notify_customer: form.notify_customer,
    notify_assigned_user: form.notify_assigned_user,
    notification_days_advance: form.notification_days_advance,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<RecurringInvoiceTemplate>) =>
      recurringInvoicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      toast.success("Recurring invoice created");
      navigate("..", { relative: "path" });
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<RecurringInvoiceTemplate>) =>
      recurringInvoicesApi.update(templateId as number, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      queryClient.invalidateQueries({
        queryKey: ["recurring-template", templateId],
      });
      queryClient.invalidateQueries({ queryKey: ["recurring-control"] });
      toast.success("Recurring invoice updated");
      navigate("../..", { relative: "path" });
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.customer) {
      toast.error("Customer is required");
      return;
    }
    const payload = buildPayload();
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = isEdit && existingLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (existingError) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading recurring invoice. Please try again.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 900, mx: "auto" }}>
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to={isEdit ? "../.." : ".."}
          relative="path"
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to Recurring Invoices
        </Button>
        <Typography level="h3">
          {isEdit ? "Edit Recurring Invoice" : "New Recurring Invoice"}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <RecurringSummary
            form={form}
            generated={existing?.occurrences_generated || 0}
          />
          <Alert color="neutral">
            Changing amounts, customer, assignment or schedule requires
            reapproval of affected upcoming occurrences. Internal notes preserve
            approvals.
          </Alert>
          {/* Details */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Details
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <FormControl required>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="e.g. Monthly Maintenance"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={6}>
                  <Box>
                    <BillingRecordPicker
                      label="Customer"
                      endpoint="/api/crm/contacts/"
                      value={form.customer}
                      onChange={(row) => setField("customer", row?.id || null)}
                    />
                  </Box>
                </Grid>
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setField("description", e.target.value)}
                      minRows={2}
                      placeholder="Optional description"
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Schedule
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <BillingRecordPicker
                  label="Invoice owner (defaults to you)"
                  endpoint="/api/users/"
                  value={form.assigned_to}
                  onChange={(row) => setField("assigned_to", row?.id || null)}
                />
                <BillingRecordPicker
                  label="Source job (optional)"
                  endpoint="/api/jobs/?include_completed=true"
                  value={form.source_job}
                  onChange={(row) => setField("source_job", row?.id || null)}
                />
                <FormControl>
                  <FormLabel>Service agreement reference (optional)</FormLabel>
                  <Input
                    value={form.agreement_reference}
                    onChange={(e) =>
                      setField("agreement_reference", e.target.value)
                    }
                  />
                </FormControl>
              </Stack>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <FormControl>
                    <FormLabel>Frequency</FormLabel>
                    <Select
                      value={form.frequency}
                      onChange={(_, value) =>
                        setField("frequency", value as RecurringFrequency)
                      }
                    >
                      <Option value="weekly">Weekly</Option>
                      <Option value="biweekly">Bi-weekly</Option>
                      <Option value="monthly">Monthly</Option>
                      <Option value="quarterly">Quarterly</Option>
                      <Option value="semiannually">Semi-annually</Option>
                      <Option value="annually">Annually</Option>
                      <Option value="custom">Custom</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} md={6}>
                  <FormControl>
                    <FormLabel>Interval</FormLabel>
                    <Input
                      type="number"
                      value={form.frequency_interval}
                      onChange={(e) =>
                        setField(
                          "frequency_interval",
                          parseInt(e.target.value, 10) || 1,
                        )
                      }
                      slotProps={{ input: { min: 1, step: 1 } }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl>
                    <FormLabel>Start Date</FormLabel>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setField("start_date", e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl>
                    <FormLabel>Next Invoice Date</FormLabel>
                    <Input
                      type="date"
                      value={form.next_invoice_date}
                      onChange={(e) =>
                        setField("next_invoice_date", e.target.value)
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl>
                    <FormLabel>End Date</FormLabel>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setField("end_date", e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={6}>
                  <FormControl>
                    <FormLabel>Max Occurrences</FormLabel>
                    <Input
                      type="number"
                      value={form.max_occurrences}
                      onChange={(e) =>
                        setField("max_occurrences", e.target.value)
                      }
                      placeholder="Leave blank for no limit"
                      slotProps={{ input: { min: 1, step: 1 } }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Amount */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Amount
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <FormControl>
                    <FormLabel>Amount Type</FormLabel>
                    <Select
                      value={form.amount_type}
                      onChange={(_, value) =>
                        setField("amount_type", value as RecurringAmountType)
                      }
                    >
                      <Option value="fixed">Fixed</Option>
                      <Option value="variable" disabled>
                        Variable
                      </Option>
                      <Option value="percentage" disabled>
                        Percentage
                      </Option>
                      <Option value="calculated" disabled>
                        Calculated
                      </Option>
                    </Select>
                  </FormControl>
                </Grid>
                {form.amount_type === "fixed" && (
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel>Fixed Subtotal (before tax)</FormLabel>
                      <Input
                        type="number"
                        value={form.fixed_amount}
                        onChange={(e) =>
                          setField("fixed_amount", e.target.value)
                        }
                        startDecorator="$"
                        slotProps={{ input: { min: 0, step: 0.01 } }}
                      />
                    </FormControl>
                  </Grid>
                )}
                {form.amount_type === "percentage" && (
                  <>
                    <Grid xs={12} md={3}>
                      <FormControl>
                        <FormLabel>Percentage Base</FormLabel>
                        <Input
                          type="number"
                          value={form.percentage_base}
                          onChange={(e) =>
                            setField("percentage_base", e.target.value)
                          }
                          startDecorator="$"
                          slotProps={{ input: { min: 0, step: 0.01 } }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <FormControl>
                        <FormLabel>Percentage Rate</FormLabel>
                        <Input
                          type="number"
                          value={form.percentage_rate}
                          onChange={(e) =>
                            setField(
                              "percentage_rate",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          endDecorator="%"
                          slotProps={{ input: { min: 0, step: 0.01 } }}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography level="title-md">Line Items</Typography>
                <Button
                  size="sm"
                  startDecorator={<Add />}
                  onClick={addLineItem}
                >
                  Add Item
                </Button>
              </Box>
              {form.line_items_template.length === 0 ? (
                <Typography level="body-sm" color="neutral">
                  No line items. Add items to itemize the recurring invoice.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {form.line_items_template.map((li, index) => (
                    <Grid
                      container
                      spacing={1}
                      key={index}
                      alignItems="flex-end"
                    >
                      <Grid xs={12} md={5}>
                        <FormControl>
                          <FormLabel
                            sx={{
                              display: {
                                xs: "block",
                                md: index === 0 ? "block" : "none",
                              },
                            }}
                          >
                            Description
                          </FormLabel>
                          <Input
                            value={li.description}
                            onChange={(e) =>
                              updateLineItem(index, {
                                description: e.target.value,
                              })
                            }
                            placeholder="Item description"
                            size="sm"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={4} md={2}>
                        <FormControl>
                          <FormLabel
                            sx={{
                              display: {
                                xs: "block",
                                md: index === 0 ? "block" : "none",
                              },
                            }}
                          >
                            Qty
                          </FormLabel>
                          <Input
                            type="number"
                            value={li.quantity}
                            onChange={(e) =>
                              updateLineItem(index, {
                                quantity: parseFloat(e.target.value) || 0,
                              })
                            }
                            size="sm"
                            slotProps={{ input: { min: 0, step: 0.01 } }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={4} md={2}>
                        <FormControl>
                          <FormLabel
                            sx={{
                              display: {
                                xs: "block",
                                md: index === 0 ? "block" : "none",
                              },
                            }}
                          >
                            Unit Price
                          </FormLabel>
                          <Input
                            type="number"
                            value={li.unit_price}
                            onChange={(e) =>
                              updateLineItem(index, {
                                unit_price: parseFloat(e.target.value) || 0,
                              })
                            }
                            startDecorator="$"
                            size="sm"
                            slotProps={{ input: { min: 0, step: 0.01 } }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={3} md={2}>
                        <FormControl>
                          <FormLabel
                            sx={{
                              display: {
                                xs: "block",
                                md: index === 0 ? "block" : "none",
                              },
                            }}
                          >
                            Tax Rate %
                          </FormLabel>
                          <Input
                            type="number"
                            value={li.tax_rate ?? ""}
                            onChange={(e) =>
                              updateLineItem(index, {
                                tax_rate:
                                  e.target.value === ""
                                    ? undefined
                                    : parseFloat(e.target.value),
                              })
                            }
                            endDecorator="%"
                            size="sm"
                            slotProps={{ input: { min: 0, step: 0.01 } }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={1} md={1} sx={{ textAlign: "right" }}>
                        <IconButton
                          color="danger"
                          variant="plain"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          aria-label="Remove line item"
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Defaults */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Invoice Defaults
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <FormControl>
                    <FormLabel>Default Payment Terms</FormLabel>
                    {/* Typed free-hand, "Net 15" was neither understood by the
                        due-date map nor a valid term on the invoice it wrote. */}
                    <Select
                      value={form.default_payment_terms}
                      onChange={(_, value) =>
                        value && setField("default_payment_terms", value)
                      }
                    >
                      {PAYMENT_TERMS.filter(
                        (term) => term.value !== "custom",
                      ).map((term) => (
                        <Option key={term.value} value={term.value}>
                          {term.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Default Notes</FormLabel>
                    <Textarea
                      value={form.default_notes}
                      onChange={(e) =>
                        setField("default_notes", e.target.value)
                      }
                      minRows={2}
                      placeholder="Notes added to each generated invoice"
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Automation & Notifications */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Automation &amp; Notifications
              </Typography>
              <Stack spacing={1.5}>
                <FormControl
                  orientation="horizontal"
                  sx={{ justifyContent: "space-between" }}
                >
                  <FormLabel>Auto-send generated invoices</FormLabel>
                  <Switch
                    checked={form.is_auto_send}
                    onChange={(e) => setField("is_auto_send", e.target.checked)}
                  />
                </FormControl>
                <FormControl
                  orientation="horizontal"
                  sx={{ justifyContent: "space-between" }}
                >
                  <FormLabel>Auto-approve generated invoices</FormLabel>
                  <Switch
                    checked={form.is_auto_approve}
                    onChange={(e) =>
                      setField("is_auto_approve", e.target.checked)
                    }
                  />
                </FormControl>
                <FormControl
                  orientation="horizontal"
                  sx={{ justifyContent: "space-between" }}
                >
                  <FormLabel>Notify assigned user</FormLabel>
                  <Switch
                    checked={form.notify_assigned_user}
                    onChange={(e) =>
                      setField("notify_assigned_user", e.target.checked)
                    }
                  />
                </FormControl>
                <Divider />
                <Typography level="body-sm">
                  Automatic sending emails the invoice on generation.
                  Assigned-user notifications appear in the notification center.
                  Advance reminders are not currently available.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Actions */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="plain"
              color="neutral"
              component={Link}
              to={isEdit ? "../.." : ".."}
              relative="path"
            >
              Cancel
            </Button>
            <Button type="submit" variant="solid" loading={isSaving}>
              {isEdit ? "Save Changes" : "Create Recurring Invoice"}
            </Button>
          </Stack>
        </Stack>
      </form>
      {templateId && <RecurringWorkspace templateId={templateId} />}
    </Box>
  );
}
